import { useMemo } from 'react';

import { DeviceBindStatus } from 'application/presenters/point/types';
import AnalyticsService, {
  StreamBindFlowEventNameAMP,
  StreamBindFlowParamNameAMP,
} from 'application/services/AnalyticsService';
import { PointParamNameAMP } from 'application/services/AnalyticsService/ampTypes';
import { useAccountCompanyDeviceRequests } from 'application/hooks';

import { DeviceTypes, RequestDeviceType } from '@shared/api/graphql/generated';

import DateUtils from '@zvuk-b2b/react-uikit/toolkit/DateUtils';

import { CurrentPointOnboardingType, DeviceTypesInFlow } from '../../types';

interface Props {
  point: CurrentPointOnboardingType;
  modalDeviceType: DeviceTypesInFlow;
  requestType: RequestDeviceType;
}

const useSberBusinessPlayerModalViewModel = ({ point, modalDeviceType }: Props) => {
  const deviceRequests = useAccountCompanyDeviceRequests();

  const requestedDevice = useMemo(() => {
    if (!deviceRequests?.length || !point?.id) {
      return null;
    }

    const targetDeviceTypeStr = String(modalDeviceType);

    const request = deviceRequests.find((request) => {
      if (request.pointId !== point.id) return false;

      if (!request.deviceType) return false;

      if (String(request.deviceType) !== targetDeviceTypeStr) return false;

      return true;
    });

    return request ?? null;
  }, [deviceRequests, point?.id, modalDeviceType]);

  const hasRecentRequest = useMemo(() => {
    if (!requestedDevice?.lastRequestDate) {
      return false;
    }

    return !DateUtils.isMoreThenDay(new Date(), new Date(requestedDevice.lastRequestDate));
  }, [requestedDevice]);

  const isAlreadyBound =
    point?.device?.bindStatus === DeviceBindStatus.BINDED && point.device?.type === modalDeviceType;

  const hasActualDeviceType = point?.device?.actualType === modalDeviceType;

  const cantRequestDevice = hasActualDeviceType || isAlreadyBound;

  const isFirstRequest = !(!!point?.device?.lastRequestTime && point.device?.type === modalDeviceType);

  const handleSuccessCallback = (deviceType: DeviceTypes) => {
    AnalyticsService.event(StreamBindFlowEventNameAMP.STREAM_PLAYER_TYPE_BOUND, {
      [StreamBindFlowParamNameAMP.PLAYER_TYPE]: deviceType,
      [PointParamNameAMP.POINT_ID]: point?.id,
    });
  };

  return {
    onSuccessCallback: handleSuccessCallback,
    hasRecentRequest,
    cantRequestDevice,
    isFirstRequest,
    requestedDevice,
  };
};

export default useSberBusinessPlayerModalViewModel;
